import videojs from "video.js";
import Evented from "@utilities/Evented";
import VAST from "./VAST";

const _maxAdBitrateKbps = 10000;
const _seekThreshold = 0.1;
const _trackingTagTimeout = 5000;

// https://watchingthat.com/guides/ima3-vast41-brightcove-jw-ad-error-codes/
const ErrorCodes = {
	VASTXMLParsingError: 100,
	VASTTimeout: 301,
	VideoPlayError: 400,
	MediaTimeout: 402,
	EmptyVASTResponse: 1009
};

const id = {};

const iOS = videojs.browser.IS_IOS;

class Ad extends Evented
{
	constructor(adPlugin, vastUrl, options)
	{
		super();

		this.ErrorCodes = ErrorCodes;

		// For debug purposes
		id[options.name] = id[options.name] || 1;
		this.name = `${options.name} ${id[options.name]++}`;

		this.log = videojs.createLogger(`Ad [${this.name}]`);
		this.log("Created");

		this.player = adPlugin.player;
		this.adContainer = adPlugin.adContainer;
		// this.companionSlots = adPlugin.companionSlots;
		this.adPlugin = adPlugin;

		// Create a video element for playing ads
		// TODO: Support for VPAID
		if (iOS)
		{
			this.videoEl = this.player.tech_.el_;
		}
		else
		{
			this.videoEl = document.createElement("video");
			this.videoEl.style.width = "100%";
			this.videoEl.style.height = "100%";
		}

		this.vastUrl = vastUrl;
		this.vast = new VAST(vastUrl);

		this.loading = false;
		this.destroyed = false;
		this.queuedPlay = false;

		// http://videojs.github.io/videojs-contrib-ads/integrator/common-interface.html
		// Default values
		this.type = "";
		this.index = 0;
		this.id = "";
		this.duration = options.duration;
		this.currentTime = () =>
		{
			return -1;
		};
		this.minDuration = -1;
		this.loaded = false;

		this.boundDisableSeekingTU = this.disableSeekingTU.bind(this);
		this.boundDisableSeekingS = this.disableSeekingS.bind(this);
		this.lastKnownTime = 0;

		this.playButtonToggle();

		// this.load();
	}

	/**
	 * Simulate the error object from the IMA SDK, for compatibility with our
	 * existing ad logic
	 */
	throwError(errorCode)
	{
		this.log.warn(`VAST error response: ${errorCode}.`);
		const errorObj = {
			getError: function()
			{
				return {
					getErrorCode: function()
					{
						return errorCode;
					}
				};
			}
		};
		this.trigger("error", errorObj);
		// After everyone has had a chance to process the error, we'll destroy the ad
		setTimeout(() =>
		{
			this.destroy();
		}, 2);
	}

	load()
	{
		// When we ACTUALLY start loading the ad (after any queued loads), we'll
		// let our other plugins know
		this.trigger("loadstart");

		this.loading = true;

		this.log(`Preloading ad: ${this.vastUrl}`);
		this.vast.load((success, loaded) =>
		{
			if (loaded)
			{
				if (!success)
				{
					this.throwError(ErrorCodes.EmptyVASTResponse);
					return;
				}

				this.log("VAST parsed. Preparing ad elements.");
				this.log(this.vast);
				this.creative = this.vast.getNextCreative();

				this.prepareAd();
			}
			else
			{
				this.throwError(ErrorCodes.EmptyVASTResponse);
				this.log("WE ARE HANDING THE THING?");
			}
		});
	}

	prepareAd()
	{
		let playerRect;
		if (this.player.el_)
		{
			playerRect = this.player.el_.getBoundingClientRect();
		}
		else
		{
			playerRect = {width: 640, height: 360};
		}

		// Simplify some later code
		this.creative.TrackingEvents.impression = this.creative.ad.Impression;

		// First remove MediaFiles that we don't support
		let filtered = this.creative.MediaFiles.filter(
			file =>
			{
				this.log.debug(`canPlayType(${file.type}) = ${this.videoEl.canPlayType(file.type)}`);
				return this.videoEl.canPlayType(file.type) === "probably";
			}
		);
		if (filtered.length === 0)
		{
			// No ads we can "probably" play, are there any we can "maybe" play?
			filtered = this.creative.MediaFiles.filter(file =>
			{
				if (videojs.browser.IS_ANY_SAFARI && file.type === "video/webm")
				{
					// QuickTime's software decoder has known issues with WEBM
					// content. If you have a hardware decoder then Safari won't
					// report the ability to play WEBM, so the ability to play
					// it implies using the software decoder, which implies
					// there will be playback issues.
					return false;
				}
				return this.player.canPlayType(file.type) === "maybe";
			});
			if (filtered.length === 0)
			{
				// Welp, can't play this ad
				this.throwError(ErrorCodes.VideoPlayError);
				return;
			}
		}

		// Remove MediaFiles that are the wrong aspect ratio for our player
		let filtered2 = filtered.filter(
			file => (file.width / file.height) === (playerRect.width / playerRect.height)
		);
		if (filtered2.length === 0)
		{
			// None matched our aspect ratio. Just check them all
			filtered2 = filtered;
		}
		filtered = filtered2;

		// Now remove any that are larger than our player
		filtered2 = filtered.filter(file => file.height <= playerRect.height);
		if (filtered2.length === 0)
		{
			// None were smaller. Just check them all.
			filtered2 = filtered;
		}
		filtered = filtered2;

		// Now remove any with a massive bitrate (> 10 Mbps)
		filtered2 = filtered.filter(file => file.bitrate < _maxAdBitrateKbps);
		if (filtered2.length === 0)
		{
			// None were small enough. Just check them all.
			filtered2 = filtered;
		}
		filtered = filtered2;

		// Finally, select based on bitrate
		// TODO: Use actual player bitrate instead of picking the highest
		filtered.sort((first, second) => second.bitrate - first.bitrate);
		this.mediaFile = filtered[0];

		this.log.debug(
			`Choosing to play: ${this.mediaFile.type} @ ${this.mediaFile.bitrate}, URL: ${this.mediaFile.uri}`
		);

		// Preload the video
		// TODO: Support for VPAID
		if (!iOS)
		{
			this.videoEl.preload = true;
			this.videoEl.src = this.mediaFile.uri;
		}

		// Set up our public properties
		this.type = "Linear";
		this.index = 1;
		this.id = this.creative.id;
		this.duration = this.creative.Duration;
		// TODO: Support skippable ads
		// this.minDuration = ...;
		this.currentTime = () =>
		{
			return this.duration - this.getRemainingTime();
		};

		this.loaded = true;
		if (this.queuedPlay)
		{
			this.play();
		}
	}

	resetEvents()
	{
		this.events = {
			impression: false,
			start: false,
			firstQuartile: false,
			midpoint: false,
			thirdQuartile: false,
			complete: false
		};
	}

	bindListeners()
	{
		this.resetEvents();
		this.eventListeners = {};
		const aEL = this.videoEl.addEventListener.bind(this.videoEl);
		aEL("timeupdate", this.eventListeners.timeupdate = () =>
		{
			this.triggerAdEvent("start");
			this.triggerAdEvent("impression");

			if (this.videoEl.currentTime / this.videoEl.duration > 1 / 4)
			{
				this.triggerAdEvent("firstQuartile");
			}
			if (this.videoEl.currentTime / this.videoEl.duration > 1 / 2)
			{
				this.triggerAdEvent("midpoint");
			}
			if (this.videoEl.currentTime / this.videoEl.duration > 3 / 4)
			{
				this.triggerAdEvent("thirdQuartile");
			}
			/* eslint-enable no-magic-numbers */
		});
		aEL("ended", this.eventListeners.ended = () => this.triggerAdEvent("complete"));
		aEL("pause", this.eventListeners.pause = () => this.triggerAdEvent("pause"));
		aEL("play", this.eventListeners.play = () => this.triggerAdEvent("resume"));

		// Fix for iOS 9
		aEL("waiting", this.eventListeners.waiting = () =>
		{
			if (this.getRemainingTime() < 1)
			{
				this.log("Buffering with remainingTime < 1, triggering 'complete'");
				this.triggerAdEvent("complete");
			}
		});
	}

	unbindListeners()
	{
		const rEL = this.videoEl.removeEventListener.bind(this.videoEl);
		for (const key in this.eventListeners)
		{
			if (Object.prototype.hasOwnProperty.call(this.eventListeners, key))
			{
				rEL(key, this.eventListeners[key]);
			}
		}
	}

	triggerAdEvent(event)
	{
		if (Object.prototype.hasOwnProperty.call(this.events, event))
		{
			// Only fire these events once
			if (this.events[event]) return;
			else this.events[event] = true;
		}

		// Log the event
		this.log.debug(`AdEvent: ${event}`);

		// Hit tracking URLs
		const tracking = this.creative.TrackingEvents[event];
		if (tracking)
		{
			for (let i = 0; i < tracking.length; i++)
			{
				this.fetch(tracking[i]);
			}
		}

		// If we've ended, handle that
		if (event === "complete")
		{
			this.onComplete();
		}

		// Tell plugins about the event (synchronously)
		this.trigger(event);
	}

	fetch(url)
	{
		// Ad tracking tags are delivered as 1x1 pixel GIFs, but often lack CORS
		// information. The only way to ensure they are consistently downloaded
		// is to put an `<img>` tag on the page
		// Of course we've got some additional concerns... Some browsers are
		// smart enough to not load an image if it won't be visible (e.g. if
		// it's behind another image), so we want to place the images in such a
		// way that they're all visible. We also want to ensure that a rogue tag
		// which loads a 500x500 pixel image doesn't cover the screen, so only 1
		// pixel of each image should be shown. Finally, we don't want the
		// screen to slowly fill up with tracking tags, so we want to hide them
		// after they are finished loading.
		// This function will handle all of that for us.

		const imgContainer = document.createElement("div");
		imgContainer.style.width = "1px";
		imgContainer.style.height = "1px";
		imgContainer.style.overflow = "none";
		imgContainer.style.display = "inline-block";

		const imgTag = document.createElement("img");
		imgTag.src = url;
		imgTag.onload = function()
		{
			imgContainer.remove();
		};

		imgContainer.appendChild(imgTag);
		this.adContainer.el_.appendChild(imgContainer);

		setTimeout(function()
		{
			imgContainer.remove();
		}, _trackingTagTimeout);
	}

	disableSeeking()
	{
		this.player.on("timeupdate", this.boundDisableSeekingTU);
		this.player.on("seeking", this.boundDisableSeekingS);
	}

	enableSeeking()
	{
		this.player.off("timeupdate", this.boundDisableSeekingTU);
		this.player.off("seeking", this.boundDisableSeekingS);
	}

	disableSeekingTU()
	{
		this.lastKnownTime = this.videoEl.currentTime;
	}

	disableSeekingS()
	{
		// Guard against infinite recursion:
		// user seeks, seeking is fired, currentTime is modified, seeking is fired, ...
		const delta = Math.abs(this.videoEl.currentTime - this.lastKnownTime);
		if (delta > _seekThreshold)
		{
			this.videoEl.currentTime = this.lastKnownTime;
		}
	}

	playButtonToggle()
	{
		// So the playToggle isnt updated during prerolls since the broadcast hasnt started yet.
		// This will let us manually change it during the prerolls while not adding extra triggers to midrolls.
		// this.name is the only thing that indicates if the ad is a preproll so well use that to check if we
		// actually need to create the trigger events.
		if (this.name.includes("Preroll"))
		{
			this.on("resume", () =>
			{
				this.player.controlBar.playToggle.removeClass("vjs-paused");
				this.player.controlBar.playToggle.addClass("vjs-playing");
			});
			this.on("pause", () =>
			{
				this.player.controlBar.playToggle.removeClass("vjs-playing");
				this.player.controlBar.playToggle.addClass("vjs-paused");
			});
		}
	}

	play()
	{
		if (this.destroyed)
		{
			this.log.error("Attempted to play a destroyed Ad");
			return;
		}

		if (this.adPlugin.ad)
		{
			this.log.error("Attempted to play an ad while one was already playing");
			return;
		}

		if (!this.adPlugin.inAdBreak())
		{
			this.log.warn("Attempted to play without initializing AdBreak");
			this.adPlugin.startLinearAdBreak();
		}

		if (!this.loaded)
		{
			// We'll play the moment we're loaded
			this.queuedPlay = true;
			return;
		}

		if (!iOS)
		{
			// Match the ad volume to the player volume
			// (not necessary on iOS since we just use the player)
			if (this.player.muted())
			{
				this.log("Content is muted. Muting ad.");
				this.setVolume(0);
			}
			else
			{
				const contentVolume = this.player.volume();
				this.log(`Content is NOT muted. Setting ad volume = ${contentVolume}`);
				this.setVolume(contentVolume);
			}
		}

		this.player.ads.ad = this;
		this.bindListeners();

		if (iOS)
		{
			// iOS can't play two videos at once
			// So instead we have to steal the content player
			this.preAdTime = this.videoEl.currentTime || 0;
			this.preAdSrc = this.videoEl.src;
			this.log(`Snapshotting content src: ${this.preAdSrc} @ ${this.preAdTime}`);
			this.videoEl.src = this.mediaFile.uri;
			this.videoEl.currentTime = 0;

			this.disableSeeking();
		}
		else
		{
			// Everyone else we just play the ad in a separate video
			this.adContainer.el_.appendChild(this.videoEl);
		}
		this.videoEl.play();
	}

	pause()
	{
		this.videoEl.pause();
	}

	resume()
	{
		if (this.getRemainingTime() < 1)
		{
			this.log("Resuming with remainingTime < 1, triggering 'complete'");
			this.triggerAdEvent("complete");
			return;
		}

		this.videoEl.play();
	}

	paused()
	{
		return this.videoEl.paused;
	}

	setVolume(value)
	{
		this.videoEl.volume = value;
	}

	getRemainingTime()
	{
		return this.videoEl.duration - this.videoEl.currentTime;
	}

	onComplete()
	{
		this.unbindListeners();

		// First, check to see if we have more creatives in the VAST
		this.creative = this.vast.getNextCreative();

		if (this.creative !== null)
		{
			// TODO: Play the next ad in the pod
			this.log.error("Ad Pods not supported yet");
		}

		// If we got here, there are no more ads to play
		// So let's clean up!

		if (iOS)
		{
			this.enableSeeking();
			if (this.preAdSrc)
			{
				// Put the content player back the way we found it
				this.log(`Restoring content src: ${this.preAdSrc} @ ${this.preAdTime}`);
				this.videoEl.src = this.preAdSrc;
				// The ad trigger plugin handles this. Don't need to do it here
				/*
				this.videoEl.curentTime = this.preAdTime;
				// The above line doesn't always seem to work. Race condition in
				// QuickTime, maybe? It works after we start playing, though.
				this.videoEl.addEventListener("playing", function updateCurrentTime()
				{
					this.videoEl.curentTime = this.preAdTime;
					this.videoEl.removeEventListener("playing", updateCurrentTime);
				});
				*/
			}
		}

		// Update the player
		if (this.player.ads.ad === this)
		{
			this.player.ads.ad = null;
		}

		this.completed = true;

		// Ads shouldn't play twice. Make sure we auto-destroy it
		// Wait 2 milliseconds instead of 1 to fire this. That way any
		// asynchronous "complete" handlers get a chance to fire
		setTimeout(() =>
		{
			this.destroy();
		}, 2);
	}

	destroy()
	{
		if (!this.completed) this.onComplete();
		if (this.destroyed) return;

		this.log("Destroying");

		// Trigger this synchronously, before this.offAll()
		this.trigger("destroyed", null, true);
		this.offAll();

		// Clean up any ad elements
		if (!iOS && this.videoEl.parentNode)
		{
			this.videoEl.parentNode.removeChild(this.videoEl);
		}
		this.videoEl = null;

		// Update the player
		if (this.player.ads.ad === this)
		{
			this.player.ads.ad = null;
		}

		this.destroyed = true;
	}
}

export default Ad;
